import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Card,
    Grid,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
    Paper,
} from '@mui/material';
import {
    IconLogout,
    IconCrown,
    IconChevronRight,
    IconPackage,
    IconMapPin,
    IconCreditCard,
    IconBell,
    IconHelpCircle,
    IconFileText,
    IconCheck,
    IconCamera,
    IconEdit,
    IconHeart,
    IconReceipt,
    IconSettings,
    IconX
} from '@tabler/icons-react';
import { colors, fonts } from '../../ColorFont/color';
import { fontThemes } from '../../ColorFont/fontTheme';
import { useNavigate } from 'react-router-dom';
import { url } from '../../ConstUrl/Url';
import { auth } from '../../auth/firebase-config/firebase';
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';
import { toast } from 'react-toastify';
import ProfileScreen from '../ProfileScreen';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton as MuiIconButton,
} from '@mui/material';

const Profile = ({ userData: propUserData }) => {
    const { t } = useTranslation(['dashboard', 'common']);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { userData: contextUserData, setUserData } = useUser();
    const fileInputRef = React.useRef(null);

    // ── User Integration ──────────────────────────────────────────────────────
    const [firebaseUser, setFirebaseUser] = React.useState(null);
    const [localUser, setLocalUser] = React.useState(propUserData || contextUserData || JSON.parse(localStorage.getItem('userData')));
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const [editFormData, setEditFormData] = React.useState(null);
    const [errors, setErrors] = React.useState({});

    // Update editFormData when localUser changes
    React.useEffect(() => {
        if (localUser) {
            setEditFormData({
                firstName: localUser.firstName || '',
                lastName: localUser.lastName || '',
                gender: localUser.gender || '',
                age: localUser.age || '',
                phoneNumber: localUser.phoneNumber || '',
                email: localUser.email || '',
                role: localUser.role || '',
                state: localUser.state || '',
                district: localUser.district || '',
                tahsil: localUser.tahsil || '',
                village: localUser.village || '',
            });
        }
    }, [localUser]);

    React.useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setFirebaseUser(user || null);
            // If we don't have localUser, try to get it again from storage
            if (!localUser) {
                const stored = JSON.parse(localStorage.getItem('userData'));
                if (stored) setLocalUser(stored);
            }
        });
        return () => unsubscribe();
    }, [localUser]);

    // Sync with propUserData or contextUserData when they change
    React.useEffect(() => {
        if (propUserData) {
            setLocalUser(propUserData);
        } else if (contextUserData) {
            setLocalUser(contextUserData);
        }
    }, [propUserData, contextUserData]);

    // Derived display values - strictly matching HeaderNav logic
    const displayName =
        (localUser?.firstName ? `${localUser.firstName} ${localUser.lastName || ''}`.trim() : null) ||
        localUser?.name ||
        localUser?.username ||
        firebaseUser?.displayName ||
        (firebaseUser?.email ? firebaseUser.email.split('@')[0] : null) ||
        (localUser?.email ? localUser.email.split('@')[0] : null) ||
        'User';

    const displayAvatar = 
        localUser?.profilePicture ? `${url}/${localUser.profilePicture.replace(/\\/g, "/")}` :
        (firebaseUser?.photoURL || localUser?.photoURL || "https://i.pravatar.cc/150?u=ankit");

    const profileData = {
        name: displayName,
        location: localUser ? ((localUser.city && localUser.state) ? `${localUser.city}, ${localUser.state}` : (localUser.city || localUser.state || "Maharashtra")) : "Maharashtra",
        isVerified: true, 
        role: localUser?.role || "User",
        profilePic: displayAvatar,
        currentPlan: (localUser?.sponseredHours > 0) ? t('dashboard:premium') : t('dashboard:standard'), 
        boostHours: `${localUser?.sponseredHours || 0} ${t('common:hours')}` 
    };

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            localStorage.removeItem('userData');
            window.location.href = '/home';
        } catch (err) {
            console.error('Sign out error:', err);
        }
    };

    const handleProfileUpdate = async () => {
        if (!localUser?._id) return;
        
        try {
            const response = await fetch(`${url}/api/users/update-profile/${localUser._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editFormData)
            });

            if (response.ok) {
                const data = await response.json();
                const updatedUser = { ...localUser, ...data.user };
                localStorage.setItem('userData', JSON.stringify(updatedUser));
                setLocalUser(updatedUser);
                setUserData(updatedUser);
                setIsEditModalOpen(false);
                toast.success(t('common:success'));
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || t('common:error'));
            }
        } catch (error) {
            console.error('Update profile error:', error);
            toast.error(t('common:error'));
        }
    };

    const handleProfilePictureChange = async (event) => {
        const file = event.target.files[0];
        if (!file || !localUser?._id) return;

        const formData = new FormData();
        formData.append('profilePicture', file);

        try {
            const response = await fetch(`${url}/api/users/${localUser._id}/profile-picture`, {
                method: 'PUT',
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                const updatedUser = { ...localUser, profilePicture: data.user.profilePicture };
                localStorage.setItem('userData', JSON.stringify(updatedUser));
                setLocalUser(updatedUser);
                setUserData(updatedUser);
                toast.success(t('common:success'));
            } else {
                toast.error(t('common:error'));
            }
        } catch (error) {
            console.error('Error updating profile picture:', error);
            toast.error(t('common:error'));
        }
    };

    const handleManageBoost = () => {
        if (profileData.role === 'Buyer') {
            navigate('/buyer/boost');
        } else {
            navigate('/farmer/boost');
        }
    };

    const actionItems = [
        { 
            label: profileData.role === 'Buyer' ? t("dashboard:viewedCrops") : t("dashboard:myListings"), 
            icon: <IconPackage size={22} />, 
            id: "listings", 
            path: profileData.role === 'Buyer' ? "/viewed-plots" : "/my-crops" 
        },
        { label: t("dashboard:wishlist"), icon: <IconHeart size={22} />, id: "wishlist", path: "/wishlist" },
        { label: t("dashboard:purchaseHistory"), icon: <IconReceipt size={22} />, id: "payment", path: "/purchase-history" },
        { label: t("dashboard:settings"), icon: <IconSettings size={22} />, id: "settings", path: "/settings" },
    ];

    const supportItems = [
        { label: t("dashboard:helpSupport"), icon: <IconHelpCircle size={22} />, id: "help", path: "/support" },
        { label: t("dashboard:termsPrivacy"), icon: <IconFileText size={22} />, id: "privacy", path: "/privacy-policy" },
    ];

    const MenuItem = ({ icon, label, onClick }) => (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                width: '100%',
                padding: '16px 20px',
                borderRadius: '16px',
                border: `1px solid ${colors.border || '#E3E8E3'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                mb: '12px',
                '&:hover': {
                    backgroundColor: '#FFFFFF',
                    borderColor: colors.primary,
                    transform: 'translateX(4px)'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Box sx={{ color: colors.text || '#6D786D', display: 'flex', alignItems: 'center' }}>
                    {icon}
                </Box>
                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 600,
                    fontSize: '15px',
                    color: colors.text_dark || '#171C17'
                }}>
                    {label}
                </Typography>
            </Box>
            <IconChevronRight size={20} color="#919EAB" />
        </Paper>
    );

    return (
        <Box sx={{
            padding: 0,
            maxWidth: '1600px',
            margin: '0 auto',
            minHeight: '100%',
            backgroundColor: '#FFFFFF', 
            display: 'flex',
            flexDirection: 'column',
            gap: '32px'
        }}>
            {/* Header & Breadcrumb */}
            <Box>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 800,
                    fontSize: isMobile ? '24px' : '30px',
                    color: colors.text_dark || '#0F1729',
                    mb: '4px'
                }}>
                    {t('dashboard:profile')}
                </Typography>
                <Typography sx={{
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#919EAB'
                }}>
                    {t('dashboard:dashboard')} &nbsp;•&nbsp; {t('dashboard:profile')}
                </Typography>
            </Box>

            {/* Profile Info Card */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '24px',
                    padding: isMobile ? '24px 20px' : '32px',
                    border: `1px solid ${colors.border || '#E3E8E3'}`,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'center', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 3,
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: '24px', textAlign: { xs: 'center', sm: 'left' } }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar
                            src={profileData.profilePic}
                            sx={{ 
                                width: 100, 
                                height: 100, 
                                border: '4px solid #FFFFFF', 
                                boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8,
                                    '& .camera-overlay': { opacity: 1 }
                                }
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {profileData.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleProfilePictureChange}
                            style={{ display: 'none' }}
                            accept="image/*"
                        />
                        <Box
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                backgroundColor: colors.light,
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${colors.border}`,
                                cursor: 'pointer',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                                zIndex: 2
                            }}
                        >
                            <IconCamera size={18} color={colors.text} />
                        </Box>
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: '12px' }}>
                            <Typography sx={{
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontWeight: 800,
                                fontSize: '22px',
                                color: colors.text_dark || '#171C17',
                                mb: '4px'
                            }}>
                                {profileData.name}
                            </Typography>
                            <IconButton 
                                onClick={() => setIsEditModalOpen(true)}
                                sx={{ 
                                    backgroundColor: '#F4F6F8', 
                                    p: '6px',
                                    '&:hover': { backgroundColor: '#DFE3E8' }
                                }}
                            >
                                <IconEdit size={16} color={colors.text} />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: '4px', mb: '12px' }}>
                            <IconMapPin size={16} color="#919EAB" />
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontSize: '14px',
                                color: colors.text || '#637381'
                            }}>
                                {profileData.location}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                            <Box sx={{
                                backgroundColor: colors.primary_rgba_8,
                                color: colors.primary,
                                padding: '4px 12px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <IconCheck size={14} stroke={3} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: fonts.poppins }}>{t('dashboard:verified')}</Typography>
                            </Box>
                            <Box sx={{
                                backgroundColor: '#FEF9C3',
                                color: '#A16207',
                                padding: '4px 12px',
                                borderRadius: '8px'
                            }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, fontFamily: fonts.poppins }}>
                                    {profileData.role?.toLowerCase() === 'buyer' || profileData.role?.toLowerCase() === 'supplier' ? t('common:buyer') : 
                                     (profileData.role?.toLowerCase() === 'farmer' || profileData.role?.toLowerCase() === 'seller' ? t('common:farmer') : 
                                      (profileData.role?.toLowerCase() === 'admin' ? t('common:admin', 'Admin') : profileData.role))}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

            </Paper>

            {/* Current Plan Card */}
            <Box sx={{
                width: '100%',
                backgroundColor: '#F8F7FF', 
                borderRadius: '24px',
                padding: '24px',
                border: '1px solid #EDEDFF',
                position: 'relative'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '20px' }}>
                    <Box sx={{ display: 'flex', gap: '16px' }}>
                        <Box sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#7F56D9',
                            boxShadow: '0px 2px 8px rgba(127, 86, 217, 0.08)'
                        }}>
                            <IconCrown size={24} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '14px', color: '#637381', fontWeight: 500, mb: '2px', fontFamily: fonts.poppins }}>{t('dashboard:currentPlanLabel')}</Typography>
                            <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#1C252E', fontFamily: fonts.poppins }}>
                                {profileData.role === 'Buyer' ? (localUser?.noOfContacts > 0 ? t('dashboard:premium') : t('dashboard:freeTrial')) : profileData.currentPlan}
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleManageBoost}
                        sx={{
                            backgroundColor: '#7F56D9',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontFamily: fonts.poppins,
                            '&:hover': { backgroundColor: '#6941C6' }
                        }}
                    >
                        {t('dashboard:manage')}
                    </Button>
                </Box>

                <Divider sx={{ mb: '16px', borderStyle: 'dashed' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '14px', color: '#637381', fontWeight: 500, fontFamily: fonts.poppins }}>
                        {profileData.role === 'Buyer' ? t("dashboard:creditsRemaining") : t("dashboard:boostHour")}
                    </Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#1C252E', fontFamily: fonts.poppins }}>
                        {profileData.role === 'Buyer' ? `${localUser?.noOfContacts || 0}` : (localUser?.sponseredHours || 0)}
                    </Typography>
                </Box>
            </Box>

            {/* Action Items List */}
            <Box>
                {actionItems.map(item => (
                    <MenuItem 
                        key={item.id} 
                        icon={item.icon} 
                        label={item.label} 
                        onClick={() => navigate(item.path)}
                    />
                ))}
            </Box>

            {/* Support Items List */}
            <Box>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 700,
                    fontSize: '16px',
                    color: colors.text_dark || '#171C17',
                    mb: '16px',
                    ml: '4px'
                }}>
                    {t('dashboard:supportSection')}
                </Typography>
                {supportItems.map(item => (
                    <MenuItem 
                        key={item.id} 
                        icon={item.icon} 
                        label={item.label} 
                        onClick={() => navigate(item.path)}
                    />
                ))}
            </Box>

            {/* Logout Button - smaller and centered */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Button
                    variant="outlined"
                    startIcon={<IconLogout size={18} />}
                    onClick={handleSignOut}
                    sx={{
                        color: '#FF5630',
                        borderColor: '#FF563033',
                        borderRadius: '12px',
                        height: '44px',
                        px: 4,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontFamily: fonts.poppins,
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                            backgroundColor: '#FF563008',
                            borderColor: '#FF5630'
                        }
                    }}
                >
                    {t('dashboard:logout')}
                </Button>
            </Box>

            {/* Edit Profile Modal */}
            <Dialog 
                open={isEditModalOpen} 
                onClose={() => setIsEditModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '24px', overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    fontFamily: fonts.poppins,
                    fontWeight: 800,
                    fontSize: '20px',
                    pb: 1
                }}>
                    {t('dashboard:editProfile', 'Edit Profile')}
                    <MuiIconButton onClick={() => setIsEditModalOpen(false)}>
                        <IconX size={20} />
                    </MuiIconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {editFormData && (
                        <ProfileScreen 
                            formData={editFormData}
                            onChange={(e) => setEditFormData({ ...editFormData, [e.target.name]: e.target.value })}
                            onSubmit={handleProfileUpdate}
                            onBack={() => setIsEditModalOpen(false)}
                            lang={localUser?.preferredLanguage || 'en'}
                            onLangChange={(l) => setEditFormData({ ...editFormData, preferredLanguage: l })}
                            role={localUser?.role}
                            errors={errors}
                            isEditMode={true} // We'll add this prop to ProfileScreen
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Profile;
